import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Copies the selected contacts into another workspace in one request.
export const BulkCopyContactsButton = ({ selectedIds, onDone }) => {
  const { workspaces = [], activeWorkspace, isBusinessEnabled } = useWorkspace() || {};
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const targets = workspaces.filter((w) => w.id !== (activeWorkspace?.id || 'personal'));
  if (!isBusinessEnabled || targets.length === 0) return null;

  const copyTo = async (w) => {
    setOpen(false);
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/contacts/bulk-copy`,
        { contact_ids: selectedIds, target_org_id: w.kind === 'org' ? w.id : null },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const skipped = data.skipped_duplicates || 0;
      toast.success(`Copied ${data.copied_count} to ${w.name}${skipped ? ` · ${skipped} already there` : ''}`);
      onDone?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not copy the selected contacts.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        disabled={selectedIds.length === 0 || busy}
        data-testid="contacts-bulk-copy-button"
        className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg px-3 py-1.5 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        {busy ? 'Copying…' : 'Copy to…'}
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50" data-testid="contacts-bulk-copy-menu">
          {targets.map((w) => (
            <button
              key={w.id}
              onClick={() => copyTo(w)}
              data-testid={`contacts-bulk-copy-to-${w.id}`}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
            >
              <span aria-hidden="true">{w.kind === 'org' ? '🏢' : '👤'}</span>
              <span className="truncate">{w.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
